import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { Constants } from '../../../utils/constants';
import PageHeader from '../../shell/PageHeader';
import { Button, Card, DataTable, DateRangeFilter, EmptyState, FilterSelect, Pagination, StatusPill, TableCard } from '../../ui';
import useClientTable from '../../../hooks/useClientTable';
import useDateRange from '../../../hooks/useDateRange';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Agent Commissions' }];

const buildColumns = ({ dateFilter, statusFilter }) => [
  { key: 'created_at', header: 'Date (WAT)', render: (row) => formatLagos(row.created_at), filter: dateFilter },
  {
    key: 'transaction_reference',
    header: 'Transaction Ref',
    render: (row) => row.transaction_reference || '—',
  },
  {
    key: 'transaction_amount',
    header: 'Txn Amount',
    align: 'right',
    render: (row) => formatNaira(row.transaction_amount),
  },
  {
    key: 'commission_amount',
    header: 'Commission',
    align: 'right',
    className: 'font-semibold',
    render: (row) => formatNaira(row.commission_amount),
  },
  { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} />, filter: statusFilter },
  { key: 'paid_at', header: 'Paid At (WAT)', render: (row) => formatLagos(row.paid_at) },
];

const AgentCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAnAgent, setNotAnAgent] = useState(false);
  const [status, setStatus] = useState('');

  const { from, to, setFrom, setTo, invalid, filter } = useDateRange();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotAnAgent(false);
    try {
      const response = await api.get(PartnerEndpoints.agentCommissions);
      setCommissions(response.data?.data?.data ?? response.data?.data ?? []);
    } catch (err) {
      if (err.response?.status === 403) setNotAnAgent(true);
      else setError('Could not load your agent commissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(
    () => filter(commissions).filter((row) => !status || row.status?.toLowerCase() === status),
    [filter, commissions, status]
  );

  const rangeTotal = useMemo(
    () => visibleRows.reduce((sum, row) => sum + (Number(row.commission_amount) || 0), 0),
    [visibleRows]
  );

  const { page, setPage, pageSize, setPageSize, pageRows, pageCount } = useClientTable(visibleRows, {
    initialPageSize: 10,
  });

  if (notAnAgent) {
    return (
      <>
        <PageHeader title="Agent Commissions" breadcrumbs={breadcrumbs} />
        <Card>
          <EmptyState
            icon={<ShieldAlert className="h-10 w-10" aria-hidden="true" />}
            title="Not a CashTap agent"
            message="Your merchant account is not registered as a CashTap agent. Contact support if you believe this is an error."
            action={
              <Link to="/layout" className="btn btn-outline-primary">
                Go to dashboard
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Agent Commissions" breadcrumbs={breadcrumbs} />

      <TableCard
        title="CashTap commissions"
        summary={{ label: 'Total commission', value: formatNaira(rangeTotal), loading }}
        toolbar={
          error && (
            <Button size="sm" onClick={load}>
              Try again
            </Button>
          )
        }
        footer={
          visibleRows.length > 0 && (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={buildColumns({
            dateFilter: <DateRangeFilter label="Date" from={from} to={to} onFromChange={setFrom} onToChange={setTo} />,
            statusFilter: (
              <FilterSelect value={status} onChange={setStatus} options={Constants.commissionStatuses} label="All" ariaLabel="Filter by status" />
            ),
          })}
          rows={pageRows}
          rowKey={(row) => row.id}
          serial
          serialStart={page * pageSize}
          loading={loading}
          error={error}
          invalidDateRange={invalid}
          empty={{
            title: 'No commissions',
            message: 'No commissions match the selected filters.',
          }}
        />
      </TableCard>
    </>
  );
};

export default AgentCommissions;
